import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { defaultTheme } from '../theme';

interface InsertTextModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (text: string) => void;
  initialText?: string;
  title?: string;
}

/**
 * Modal for inserting text into the transcript
 * Allows users to input text to be inserted after a selected element
 */
const InsertTextModal: React.FC<InsertTextModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  initialText = '',
  title = 'Insert Text'
}) => {
  const [text, setText] = useState(initialText);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  
  // Focus the input field when the modal opens
  useEffect(() => {
    if (isOpen && textInputRef.current) {
      textInputRef.current.focus();
      textInputRef.current.select();
    }
  }, [isOpen]);
  
  // Reset text state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setText(initialText);
    }
  }, [isOpen, initialText]);
  
  // Handle confirm button click
  const handleConfirm = () => {
    if (text.trim()) {
      onConfirm(text);
      onClose();
    }
  };
  
  // Handle submit on Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleConfirm();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };
  
  if (!isOpen) return null;
  
  // Prevent any mouse event from propagating or affecting selection
  const preventSelectionChange = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  };

  return (
    <ModalOverlay 
      onClick={onClose}
      onMouseDown={preventSelectionChange}
      onMouseUp={preventSelectionChange}
      onMouseEnter={preventSelectionChange}
      onMouseLeave={preventSelectionChange}
      onMouseMove={preventSelectionChange}
    >
      <ModalContainer 
        onClick={e => e.stopPropagation()}
        onMouseDown={preventSelectionChange}
        onMouseUp={preventSelectionChange}
        data-selection-menu="true"
      >
        <ModalHeader onMouseDown={preventSelectionChange} onMouseUp={preventSelectionChange}>
          <ModalTitle>{title}</ModalTitle>
        </ModalHeader>
        
        <ModalBody onMouseDown={preventSelectionChange} onMouseUp={preventSelectionChange}>
          <TextArea
            ref={textInputRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter text or punctuation to insert..."
            rows={4}
            // Allow mouse events in the textarea itself
            onMouseDown={e => e.stopPropagation()}
            onMouseUp={e => e.stopPropagation()}
          />
          
          <InfoText>
            Press Enter to confirm, or Shift+Enter for a new line.
            Punctuation will be inserted without timing information.
          </InfoText>
        </ModalBody>
        
        <ModalFooter onMouseDown={preventSelectionChange} onMouseUp={preventSelectionChange}>
          <CancelButton 
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            onMouseDown={preventSelectionChange}
            onMouseUp={preventSelectionChange}
          >
            Cancel
          </CancelButton>
          <ConfirmButton 
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleConfirm();
            }}
            onMouseDown={preventSelectionChange}
            onMouseUp={preventSelectionChange}
            disabled={!text.trim()}
          >
            Insert
          </ConfirmButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default InsertTextModal;

// Styled components
const ModalOverlay = styled.div.attrs({ className: 'modal-overlay' })`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContainer = styled.div.attrs({ className: 'modal-container' })`
  background-color: ${defaultTheme.colors.background};
  border-radius: ${defaultTheme.borderRadius.md};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  width: 500px;
  max-width: 90vw;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.2s ease-out;
  
  @keyframes slideIn {
    from { transform: translateY(-20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const ModalHeader = styled.div`
  padding: ${defaultTheme.spacing.md};
  border-bottom: 1px solid ${defaultTheme.colors.border};
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: ${defaultTheme.colors.text};
  font-size: ${defaultTheme.fontSizes.medium};
  font-weight: 600;
`;

const ModalBody = styled.div`
  padding: ${defaultTheme.spacing.md};
`;

const TextArea = styled.textarea`
  width: 95%;
  padding: ${defaultTheme.spacing.sm};
  background-color: ${defaultTheme.colors.backgroundSecondary};
  color: ${defaultTheme.colors.text};
  border: 1px solid ${defaultTheme.colors.border};
  border-radius: ${defaultTheme.borderRadius.sm};
  font-size: ${defaultTheme.fontSizes.regular};
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: ${defaultTheme.colors.active};
    box-shadow: 0 0 0 2px rgba(76, 154, 255, 0.3);
  }
`;

const InfoText = styled.p`
  margin: ${defaultTheme.spacing.sm} 0 0;
  color: ${defaultTheme.colors.textSecondary};
  font-size: ${defaultTheme.fontSizes.small};
`;

const ModalFooter = styled.div`
  padding: ${defaultTheme.spacing.md};
  border-top: 1px solid ${defaultTheme.colors.border};
  display: flex;
  justify-content: flex-end;
  gap: ${defaultTheme.spacing.sm};
`;

const Button = styled.button`
  padding: ${defaultTheme.spacing.xs} ${defaultTheme.spacing.md};
  border-radius: ${defaultTheme.borderRadius.sm};
  font-size: ${defaultTheme.fontSizes.small};
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: transparent;
  color: ${defaultTheme.colors.textSecondary};
  border: 1px solid ${defaultTheme.colors.border};
  
  &:hover:not(:disabled) {
    background-color: rgba(40, 40, 60, 0.8);
    color: ${defaultTheme.colors.text};
  }
`;

const ConfirmButton = styled(Button)`
  background-color: ${defaultTheme.colors.active};
  color: white;
  border: 1px solid ${defaultTheme.colors.active};
  
  &:hover:not(:disabled) {
    background-color: #3a8cff;
    border-color: #3a8cff;
  }
`;